import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

import { EMPTY_STRING } from '@e-socna/empty';
import { getFragmentData } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { MissionAccountingPage_PennylaneQuote_Fragment } from './hooks/useMissionPennylaneAccountingQuery.hook';
import { pennylaneQuoteStatusLabels } from './utils/pennylane-enum-labels.util';
import { usePennylaneAmountFormatter } from './hooks/usePennylaneAmountFormatter.hook';
import { PennylaneInvoiceAccordion } from './PennylaneInvoiceAccordion';
import { formatDate } from './utils/format-date.util';

export const PennylaneQuoteAccordion = ({
  quoteRef,
  defaultExpanded = false,
}: Readonly<{
  quoteRef: FragmentType<typeof MissionAccountingPage_PennylaneQuote_Fragment>;
  defaultExpanded?: boolean;
}>) => {
  const formatPennylaneAmount = usePennylaneAmountFormatter();
  const quote = getFragmentData(
    MissionAccountingPage_PennylaneQuote_Fragment,
    quoteRef,
  );
  const invoices = quote.resolvedLinkedInvoices ?? [];

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RequestQuoteIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2">
            Devis Pennylane{' '}
            {quote.quote_number ? `N° ${quote.quote_number} ` : EMPTY_STRING}
            <Typography color="text.secondary" variant="caption">
              (ID {String(quote.id)})
            </Typography>
          </Typography>
          {!!quote.status && (
            <Chip
              label={pennylaneQuoteStatusLabels[quote.status] ?? quote.status}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3 }}>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Montant HT
              </Typography>
              <Typography variant="body2">
                {formatPennylaneAmount(
                  quote.currency_amount_before_tax,
                  quote.currency,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Montant TTC
              </Typography>
              <Typography variant="body2">
                {formatPennylaneAmount(quote.currency_amount, quote.currency)}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Date
              </Typography>
              <Typography variant="body2">{formatDate(quote.date)}</Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Échéance
              </Typography>
              <Typography variant="body2">
                {formatDate(quote.deadline)}
              </Typography>
            </Box>
            {!!quote.external_reference && (
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Référence e-SOCNA
                </Typography>
                <Typography variant="body2">
                  {quote.external_reference}
                </Typography>
              </Box>
            )}
          </Stack>

          {!!invoices.length && (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography gutterBottom variant="subtitle2">
                Factures liées ({invoices.length})
              </Typography>
              <Stack spacing={1}>
                {invoices.map((invoice) => (
                  <PennylaneInvoiceAccordion
                    invoice={invoice}
                    key={String(invoice.id)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {invoices.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              Aucune facture liée à ce devis.
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
